import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { products, CATEGORIES } from "@/mocks/products";
import { useCart, getShippingCost, getFreeShippingThreshold } from "@/hooks/useCart";
import { useCurrency, CURRENCIES } from "@/hooks/useCurrency";
import type { CurrencyCode } from "@/hooks/useCurrency";

const DISCOUNT_OPTIONS = [0, 0.05, 0.1];

export default function Home() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(
    () => new Set(products.map((p) => p.id))
  );
  const [cartOpen, setCartOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(1800);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const [customDiscountValue, setCustomDiscountValue] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  const { currency, setCurrency, info, formatPrice, getShareUrl } = useCurrency();
  const cart = useCart(currency);

  const getVariantPrice = useCallback(
    (variant: { price: number; audPrice: number }) => {
      return currency === "AUD" ? variant.audPrice : variant.price;
    },
    [currency]
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setCurrencyOpen(false);
      }
    }
    if (currencyOpen) {
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }
  }, [currencyOpen]);

  const getPriceRange = useCallback(
    (variants: { price: number; audPrice: number }[]) => {
      const prices = variants.map((v) => getVariantPrice(v));
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (min === max) return formatPrice(min);
      return `${formatPrice(min)} – ${formatPrice(max)}`;
    },
    [formatPrice, getVariantPrice]
  );

  const buildOrderText = useCallback(() => {
    const sep = "═".repeat(36);
    const thin = "─".repeat(36);

    const lines: string[] = [
      `🧬 PeptiLab — Order Information`,
      sep,
      "",
    ];

    const maxNameLen = Math.max(...cart.items.map((i) => i.familyName.length), 0);

    cart.items.forEach((i) => {
      const name = i.familyName.padEnd(maxNameLen);
      const qty = String(i.quantity).padStart(2);
      const priceLabel = i.isGift ? "FREE GIFT" : formatPrice(i.price);
      lines.push(`  ${name}  | ${i.code} | ${i.spec} | ${priceLabel} × ${qty}`);
    });

    const giftItems = cart.items.filter((i) => i.isGift);
    const giftCount = giftItems.reduce((sum, i) => sum + i.quantity, 0);

    lines.push("");
    lines.push(thin);

    const padLabel = 12;
    const subtotalStr = formatPrice(cart.subtotal);
    lines.push(`  ${"Subtotal:".padEnd(padLabel)} ${subtotalStr}`);

    if (giftCount > 0) {
      const giftCodes = giftItems.map((g) => `${g.code}×${g.quantity}`).join(", ");
      lines.push(`  ${"Gift Items:".padEnd(padLabel)} ${giftCodes}`);
    }

    const discountStr = formatPrice(cart.discount);
    const discountPct = Math.round(cart.discountRate * 100);
    if (cart.discount > 0) {
      lines.push(`  ${`Discount (${discountPct}%):`.padEnd(padLabel)} -${discountStr}`);
    }

    const shippingStr =
      cart.shipping === 0 ? "FREE 🎉" : formatPrice(cart.shipping);
    lines.push(`  ${"Shipping:".padEnd(padLabel)} ${shippingStr}`);

    lines.push(thin);
    lines.push(`  ${"TOTAL:".padEnd(padLabel)} ${formatPrice(cart.totalPrice)}`);

    if (cart.shipping > 0) {
      const remaining = getFreeShippingThreshold(currency) - cart.subtotal;
      lines.push("");
      lines.push(
        `  💡 Add ${formatPrice(remaining)} more for FREE shipping!`
      );
    }

    return lines.join("\n");
  }, [cart.items, cart.subtotal, cart.discount, cart.discountRate, cart.shipping, cart.totalPrice, formatPrice]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const s = search.trim().toLowerCase();
      const matchesSearch =
        s === "" ||
        p.name.toLowerCase().includes(s) ||
        p.variants.some((v) => v.code.toLowerCase().includes(s));
      const matchesCategory =
        activeCategory === "all" || p.categories.includes(activeCategory);
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);

  const toggleFamily = useCallback((id: string) => {
    setExpandedFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  }, []);

  const openConfirm = useCallback(() => {
    if (cart.items.length === 0) return;
    setCartOpen(false);
    setConfirmOpen(true);
    setCountdown(1800);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [cart.items.length]);

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const copyOrder = useCallback(async () => {
    const text = buildOrderText();
    try {
      await navigator.clipboard.writeText(text);
      showToast("Order copied! Please send to your dedicated manager on WhatsApp");
    } catch {
      showToast("Copy failed — please copy manually");
    }
  }, [buildOrderText, showToast]);

  const copyShareLink = useCallback(async (code: CurrencyCode) => {
    const url = getShareUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      showToast(`${code} link copied!`);
    } catch {
      showToast("Copy failed — please copy manually");
    }
  }, [getShareUrl, showToast]);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* Top Bar */}
      <header className="bg-primary-500 text-background-50 py-6 md:py-8 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl md:text-2xl font-heading font-semibold tracking-tight">
            🧬 PeptiLab
          </h1>
          <p className="mt-1 text-sm md:text-base text-background-200/80">
            Professional Grade · Quality Assured
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-400/40 text-xs text-background-100">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-500" />
            1 Box = 10 vials
            <span className="text-background-200/60">·</span>
            <div className="relative" ref={currencyDropdownRef}>
              <button
                onClick={() => setCurrencyOpen(!currencyOpen)}
                className="flex items-center gap-0.5 cursor-pointer hover:text-background-50 transition-colors"
              >
                {info.flag} {info.symbol} {info.code}
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${currencyOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {currencyOpen && (
                <div className="absolute top-full mt-1.5 right-0 bg-background-50 rounded-lg shadow-lg border border-background-200 py-1 z-50 min-w-[110px]">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => {
                        if (currency !== c.code) {
                          setCurrency(c.code);
                          cart.clearCart();
                          showToast(`Switched to ${c.code} — cart cleared`);
                        }
                        setCurrencyOpen(false);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-sm whitespace-nowrap cursor-pointer transition-colors ${
                        currency === c.code
                          ? "bg-primary-100 text-primary-700 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {c.flag} {c.symbol} {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sticky Search + Categories */}
      <div className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-sm border-b border-background-200/60">
        {/* Search */}
        <div className="px-4 pt-3 pb-2 md:py-4">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base md:text-lg" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or code..."
                className="w-full pl-10 md:pl-11 pr-10 py-2 md:py-2.5 rounded-lg border border-background-200 bg-background-100/50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500 focus:bg-background-50 transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors rounded-full hover:bg-background-200"
                >
                  <i className="ri-close-line text-base" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Category Nav - horizontal scroll */}
        <div className="pb-2.5">
          <div className="flex gap-1.5 overflow-x-auto px-4 no-scrollbar justify-center">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer shrink-0 ${
                  activeCategory === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200 active:bg-background-200"
                }`}
              >
                <span className="mr-1">{cat.emoji}</span>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Product List */}
      <main className="flex-1 px-4 py-3 pb-24">
        <div className="max-w-5xl mx-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16 text-foreground-500">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center text-foreground-400">
                <i className="ri-inbox-line text-2xl" />
              </div>
              <p className="text-sm">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProducts.map((family) => {
                const isOpen = expandedFamilies.has(family.id);
                return (
                  <div
                    key={family.id}
                    className="bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-background-300/80 transition-all duration-200 group"
                  >
                    {/* Card Body */}
                    <div>
                      {/* Header - clickable */}
                      <button
                        onClick={() => toggleFamily(family.id)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-background-100/50 active:bg-background-100 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-foreground-950 line-clamp-2">
                            {family.name}
                          </span>
                          <span className="text-xs font-medium text-accent-600 mt-0.5 block">
                            {getPriceRange(family.variants)}
                          </span>
                        </div>
                        <div className="w-6 h-6 flex items-center justify-center shrink-0 text-foreground-500 ml-2">
                          <i
                            className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                      </button>

                      {/* Variants */}
                      <div
                        className={`transition-all duration-200 overflow-hidden ${isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}
                      >
                        <div className="border-t border-background-200/60">
                          {family.variants.map((variant) => {
                            const cartItem = cart.items.find(
                              (i) => i.code === variant.code && !i.isGift
                            );
                            const giftItem = cart.items.find(
                              (i) => i.code === variant.code && i.isGift
                            );
                            const isGiftable = variant.code === "BAC3(BA3)" || variant.code === "BAC10(BA10)";
                            return (
                              <div
                                key={variant.code}
                                className="flex items-center gap-2 px-4 py-3 hover:bg-background-100/40 transition-colors"
                              >
                                <span className="text-[10px] md:text-[11px] font-mono text-foreground-500 min-w-[56px] max-w-[112px] shrink-0 break-all">
                                  {variant.code}
                                </span>
                                <span className="text-xs text-foreground-700 flex-1 min-w-0 truncate">
                                  {variant.spec}
                                </span>
                                <span className="text-xs font-semibold text-accent-600 whitespace-nowrap shrink-0">
                                  {formatPrice(getVariantPrice(variant))}
                                </span>
                                {cartItem ? (
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button
                                      onClick={() =>
                                        cart.updateQuantity(
                                          variant.code,
                                          cartItem.quantity - 1,
                                          false
                                        )
                                      }
                                      className="w-6 h-6 flex items-center justify-center rounded-md bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors cursor-pointer"
                                    >
                                      <i className="ri-subtract-line text-xs" />
                                    </button>
                                    <span className="text-xs font-medium text-foreground-950 w-4 text-center">
                                      {cartItem.quantity}
                                    </span>
                                    <button
                                      onClick={() =>
                                        cart.updateQuantity(
                                          variant.code,
                                          cartItem.quantity + 1,
                                          false
                                        )
                                      }
                                      className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                                    >
                                      <i className="ri-add-line text-xs" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => {
                                      cart.addItem({
                                        familyName: family.name,
                                        code: variant.code,
                                        spec: variant.spec,
                                        price: getVariantPrice(variant),
                                      });
                                      showToast("Added to cart");
                                    }}
                                    className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 active:scale-90 transition-all shrink-0 cursor-pointer"
                                  >
                                    <i className="ri-add-line text-xs" />
                                  </button>
                                )}
                                {isGiftable && (
                                  <button
                                    onClick={() => {
                                      cart.addGiftItem({
                                        familyName: family.name,
                                        code: variant.code,
                                        spec: variant.spec,
                                      });
                                      showToast("Free gift added!");
                                    }}
                                    className="w-7 h-7 flex items-center justify-center rounded-md bg-amber-500 text-background-50 hover:bg-amber-600 active:scale-90 transition-all shrink-0 cursor-pointer"
                                    title="Add as free gift"
                                  >
                                    <i className="ri-gift-line text-xs" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-background-100 border-t border-background-200 py-8 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm font-semibold text-foreground-950">Contact us</p>
          <p className="mt-1 text-sm text-foreground-600">
            WhatsApp for inquiries & orders
          </p>
          <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-foreground-600">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-send-line text-sm" />
            </div>
            Contact & About
          </div>
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-accent-600 hover:text-accent-700 transition-colors">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-bar-chart-box-line text-sm" />
            </div>
            <span className="cursor-pointer">Stock List</span>
          </div>
          {/* Share Link */}
          <div className="mt-4 pt-4 border-t border-background-200">
            <p className="text-xs text-foreground-500 mb-2">Share price list in:</p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => copyShareLink(c.code)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-700 hover:bg-background-200 hover:border-background-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>{c.flag}</span>
                  <span>{c.code}</span>
                  <div className="w-3 h-3 flex items-center justify-center text-foreground-400">
                    <i className="ri-link text-[10px]" />
                  </div>
                </button>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-foreground-400">
              Click to copy a link that opens in the selected currency
            </p>
          </div>
          <p className="mt-4 text-xs text-foreground-400">v.20</p>
        </div>
      </footer>

      {/* Cart FAB */}
      <button
        onClick={() => setCartOpen(true)}
        className="fixed bottom-5 sm:bottom-6 right-4 sm:right-6 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary-500 text-foreground-950 flex items-center justify-center hover:bg-primary-400 active:scale-95 transition-all z-50 cursor-pointer"
      >
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-shopping-cart-line text-lg" />
        </div>
        {cart.totalCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent-500 text-background-50 text-xs font-bold flex items-center justify-center">
            {cart.totalCount}
          </span>
        )}
      </button>

      {/* Cart Modal Overlay */}
      {cartOpen && (
        <div
          className="fixed inset-0 bg-foreground-950/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setCartOpen(false)}
        >
          <div
            className="w-full max-w-md bg-background-50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cart Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center text-foreground-700">
                  <i className="ri-shopping-cart-line text-lg" />
                </div>
                <span className="text-base font-semibold text-foreground-950">
                  Shopping Cart
                </span>
              </div>
              <button
                onClick={() => setCartOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {cart.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-foreground-500">
                  <div className="w-12 h-12 flex items-center justify-center mb-3">
                    <i className="ri-shopping-basket-line text-3xl text-foreground-300" />
                  </div>
                  <p className="text-sm">Cart is empty</p>
                </div>
              ) : (
                <div className="space-y-0">
                  {cart.items.map((item) => (
                    <div
                      key={`${item.code}-${item.isGift ? "gift" : "normal"}`}
                      className="flex items-center justify-between py-3 border-b border-background-200/60 last:border-b-0"
                    >
                      <div className="flex-1 min-w-0 pr-3">
                        <p className="text-sm font-medium text-foreground-950">
                          {item.code} — {item.familyName}
                          {item.isGift && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">
                              <i className="ri-gift-line text-[10px]" />
                              Free Gift
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {item.spec} · {item.isGift ? "Free" : `${formatPrice(item.price)}/box`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              cart.updateQuantity(item.code, item.quantity - 1, item.isGift)
                            }
                            className="w-7 h-7 flex items-center justify-center rounded-full border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-subtract-line text-sm" />
                          </button>
                          <span className="text-sm font-medium text-foreground-950 w-5 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              cart.updateQuantity(item.code, item.quantity + 1, item.isGift)
                            }
                            className="w-7 h-7 flex items-center justify-center rounded-full border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-sm" />
                          </button>
                        </div>
                        <span className="text-sm font-semibold text-foreground-950 w-14 text-right">
                          {item.isGift ? "Free" : formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {cart.items.length > 0 && (
              <div className="px-5 py-4 border-t border-background-200">
                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground-600">Subtotal</span>
                    <span className="text-sm font-medium text-foreground-950">
                      {formatPrice(cart.subtotal)}
                    </span>
                  </div>
                  {cart.discount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-accent-600">Discount ({Math.round(cart.discountRate * 100)}%)</span>
                      <span className="text-sm font-medium text-accent-600">
                        -{formatPrice(cart.discount)}
                      </span>
                    </div>
                  )}
                  {cart.items.filter((i) => i.isGift).length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-amber-600">
                        <i className="ri-gift-line text-xs mr-1" />
                        Gift Items ({cart.items.filter((i) => i.isGift).reduce((s, i) => s + i.quantity, 0)})
                      </span>
                      <span className="text-sm font-medium text-amber-600">Free</span>
                    </div>
                  )}
                  {cart.shipping > 0 ? (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-foreground-500">
                          {formatPrice(getFreeShippingThreshold(currency) - cart.subtotal)} away from free shipping
                        </span>
                        <span className="text-xs font-medium text-accent-600">
                          {Math.round((cart.subtotal / getFreeShippingThreshold(currency)) * 100)}%
                        </span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-background-200 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent-500 transition-all duration-300"
                          style={{
                            width: `${Math.min(
                              (cart.subtotal / getFreeShippingThreshold(currency)) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="flex items-center gap-1.5 text-xs text-accent-600 font-medium">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-double-line text-sm" />
                        </div>
                        You've unlocked free shipping!
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground-600">Shipping</span>
                    <span className="text-sm font-medium text-foreground-950">
                      {cart.shipping === 0 ? (
                        <span className="text-accent-600">FREE</span>
                      ) : (
                        formatPrice(cart.shipping)
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-1.5 border-t border-background-200/60">
                    <span className="text-sm font-semibold text-foreground-950">
                      Total
                    </span>
                    <span className="text-lg font-bold text-accent-500">
                      {formatPrice(cart.totalPrice)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={openConfirm}
                    className="flex-1 py-2.5 rounded-lg bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-sm" />
                      </div>
                      Confirm Order
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      cart.clearCart();
                      showToast("Cart cleared");
                    }}
                    className="py-2.5 px-4 rounded-lg border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-delete-bin-line text-sm" />
                      </div>
                      Clear All
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 bg-foreground-950/50 z-[60] flex items-center justify-center p-4"
          onClick={closeConfirm}
        >
          <div
            className="w-full max-w-md bg-background-50 rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Badge */}
            <div className="px-6 pt-6 text-center">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-flashlight-line text-xs" />
                </div>
                LIMITED TIME OFFER
              </div>
            </div>

            {/* Title */}
            <div className="px-6 pt-3 text-center">
              <h3 className="text-lg font-heading font-semibold text-foreground-950">
                Hurry! Your Discount Is Ticking
              </h3>
            </div>

            {/* Countdown */}
            <div className="px-6 pt-4">
              <div className="text-center p-4 rounded-xl bg-background-100 border border-background-200">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                  OFFER EXPIRES IN
                </p>
                <p className="text-3xl font-bold text-accent-600 mt-1 font-mono">
                  {formatCountdown(countdown)}
                </p>
              </div>
            </div>

            {/* Offer */}
            <div className="px-6 pt-3 text-center">
              <div className="inline-flex items-center gap-1.5 text-sm text-accent-700 font-medium mb-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-gift-line text-sm" />
                </div>
                Limited Time Offer
              </div>
              <div className="flex items-center justify-center gap-1 flex-wrap">
                {DISCOUNT_OPTIONS.map((rate) => {
                  const pct = Math.round(rate * 100);
                  const isActive = cart.discountRate === rate && customDiscountValue === "";
                  return (
                    <button
                      key={rate}
                      onClick={() => {
                        cart.setDiscountRate(rate);
                        setCustomDiscountValue("");
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200"
                      }`}
                    >
                      {pct === 0 ? "No Discount" : `${pct}% Off`}
                    </button>
                  );
                })}
                <div className="inline-flex items-center gap-1">
                  <input
                    ref={customInputRef}
                    type="number"
                    min="0"
                    max="50"
                    value={customDiscountValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") {
                        setCustomDiscountValue("");
                        cart.setDiscountRate(0);
                        return;
                      }
                      const num = parseFloat(v);
                      if (isNaN(num) || num < 0 || num > 50) return;
                      setCustomDiscountValue(v);
                      cart.setDiscountRate(num / 100);
                    }}
                    placeholder="Custom"
                    className="w-16 px-2.5 py-1.5 rounded-full border border-background-200 bg-background-50 text-xs text-foreground-950 text-center focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500"
                  />
                  <span className="text-xs text-foreground-500">%</span>
                </div>
                <p className="text-[10px] text-foreground-400 mt-1.5">Max discount: 50%</p>
              </div>
              {cart.discount > 0 && (
                <p className="text-xs text-foreground-500 mt-2">
                  You save <strong className="text-accent-600">{formatPrice(cart.discount)}</strong> on this order
                </p>
              )}
            </div>

            {/* Hint */}
            <div className="px-6 pt-4 text-center">
              <p className="text-sm text-foreground-700">
                Please copy the order list and send it to
                <br />
                your dedicated customer service representative ASAP
              </p>
            </div>

            {/* Policy Reminders */}
            <div className="px-6 pt-3">
              <div className="bg-background-100 rounded-xl border border-background-200 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5 text-accent-500">
                    <i className="ri-ship-line text-xs" />
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    FedEx shipping at <strong className="text-foreground-800">{formatPrice(getShippingCost(currency))}</strong>. Free over {formatPrice(getFreeShippingThreshold(currency))}.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5 text-primary-500">
                    <i className="ri-shield-check-line text-xs" />
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Customs detention? We offer <strong className="text-foreground-800">one free re-shipment</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5 text-secondary-500">
                    <i className="ri-user-star-line text-xs" />
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Each customer has <strong className="text-foreground-800">one dedicated account representative</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* Copy Area */}
            <div className="px-6 pt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-foreground-800">
                  Order Information
                </span>
                <span className="text-[10px] text-foreground-500">
                  Tap to select & copy
                </span>
              </div>
              <textarea
                readOnly
                value={buildOrderText()}
                onClick={(e) => {
                  (e.target as HTMLTextAreaElement).select();
                  copyOrder();
                }}
                className="w-full h-32 p-3 rounded-lg border border-background-200 bg-background-100 text-xs text-foreground-700 font-mono resize-none focus:outline-none focus:ring-2 focus:ring-accent-500/30"
              />
            </div>

            {/* Actions */}
            <div className="px-6 pt-3 pb-6 space-y-2">
              <button
                onClick={copyOrder}
                className="w-full py-2.5 rounded-lg bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-sm" />
                  </div>
                  Copy Order Info
                </div>
              </button>
              <button
                onClick={closeConfirm}
                className="w-full py-2.5 rounded-lg border border-background-300 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[70] animate-fade-in-up">
          <div className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium shadow-lg flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-check-line text-sm" />
            </div>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}